import React, { useMemo, useState } from "react";
import { Plus, Landmark } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSepaBankAccounts } from "../../../../stores/Sepa/sepa.queries";
import {
  useDeleteSepaBankAccount,
  useDeleteSepaMandate,
  useGenerateSepaMandateForm,
  useRevokeSepaMandate,
  useUploadSepaMandateSignedDocument,
} from "../../../../stores/Sepa/sepa.mutations";
import { useUserAccounts } from "../../../../stores/User/User.hooks";
import { useApi } from "../../../../api/ApiProvider";
import { formatIbanForDisplay } from "../../../../stores/Sepa/sepa.validation";
import {
  BankAccountWithMandatesDto,
  MandateSummaryDto,
  MandateDocumentVersionDto,
  MandateStatusEnum,
} from "../../../../stores/Sepa/sepa.types";
import MandateFormDialog from "./MandateFormDialog";
import MandateEditDialog from "./MandateEditDialog";
import RevokeMandateDialog, { RevokeMandateInput } from "./RevokeMandateDialog";
import SepaMandateRow from "./SepaMandateRow";
import SignedMandateUploadDialog, { SignedMandateUpload } from "./SignedMandateUploadDialog";

interface Props {
  patientId: number;
}

interface ConfirmState {
  title: string;
  description: string;
  action: () => void;
}

/**
 * Card that manages a patient's SEPA bank accounts and their mandates. Replaces the
 * legacy debit + credit permission cards: one place to add a recurring mandate,
 * download the fillable form for signing, revoke/delete mandates, and remove a
 * stored bank account.
 */
const SepaBankAccountsCard: React.FC<Props> = ({ patientId }) => {
  const { data: bankAccounts = [], isLoading } = useSepaBankAccounts(patientId);
  const userAccounts = useUserAccounts();
  const accounts = useMemo(() => userAccounts ?? [], [userAccounts]);
  const { documents: documentsApi } = useApi();

  const generateForm = useGenerateSepaMandateForm();
  const revokeMandate = useRevokeSepaMandate(patientId);
  const deleteMandate = useDeleteSepaMandate(patientId);
  const deleteBank = useDeleteSepaBankAccount(patientId);
  const uploadSigned = useUploadSepaMandateSignedDocument(patientId);

  const [mandateDialogOpen, setMandateDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activate, setActivate] = useState<{
    mandate: MandateSummaryDto;
    account: BankAccountWithMandatesDto;
  } | null>(null);
  const [edit, setEdit] = useState<{
    mandate: MandateSummaryDto;
    account: BankAccountWithMandatesDto;
  } | null>(null);
  const [revoke, setRevoke] = useState<MandateSummaryDto | null>(null);

  const accountNameById = useMemo(() => {
    const map = new Map<string, string>();
    accounts.forEach((a) => map.set(a.id, a.name));
    return map;
  }, [accounts]);

  const busy =
    generateForm.isPending ||
    revokeMandate.isPending ||
    deleteMandate.isPending ||
    deleteBank.isPending ||
    uploadSigned.isPending;

  // Draft/Active mandates are the working set; everything else (used, revoked,
  // expired) is considered archived and only shown when the toggle is on.
  const isActiveStatus = (status: MandateSummaryDto["status"]) =>
    status === MandateStatusEnum.Draft || status === MandateStatusEnum.Active;

  const rows = bankAccounts.flatMap((account) => {
    if (account.mandates.length === 0) {
      // Bank verbindungen without any mandate belong to the working set only.
      return showArchived ? [] : [{ account, mandate: null as MandateSummaryDto | null }];
    }
    return account.mandates
      .filter((mandate) =>
        showArchived ? !isActiveStatus(mandate.status) : isActiveStatus(mandate.status),
      )
      .map((mandate) => ({ account, mandate }));
  });

  const handleDownloadForm = (mandate: MandateSummaryDto) => {
    generateForm.mutate(mandate.id, {
      onSuccess: (doc) => {
        if (doc?.accessUrl) window.open(doc.accessUrl, "_blank");
      },
    });
  };

  const handleDownloadVersion = (
    _mandate: MandateSummaryDto,
    version: MandateDocumentVersionDto,
  ) => {
    if (!version.documentId) return;
    documentsApi
      .requestAccessForDocument(version.documentId)
      .then((doc) => {
        const url = (doc as { accessUrl?: string | null })?.accessUrl;
        if (url) window.open(url, "_blank");
      })
      .catch(() => undefined);
  };

  const submitActivation = (upload: SignedMandateUpload) => {
    if (!activate) return;
    uploadSigned.mutate(
      {
        id: activate.mandate.id,
        file: upload.file,
        signatureDate: upload.signatureDate,
        confirmed: upload.confirmed,
      },
      { onSuccess: () => setActivate(null) },
    );
  };

  const handleRevoke = (mandate: MandateSummaryDto) => {
    setRevoke(mandate);
  };

  const submitRevoke = (input: RevokeMandateInput) => {
    if (!revoke) return;
    revokeMandate.mutate({ id: revoke.id, dto: input }, { onSuccess: () => setRevoke(null) });
  };

  const handleDeleteMandate = (mandate: MandateSummaryDto) => {
    setConfirm({
      title: "Mandat löschen?",
      description: "Das Mandat wird aus der Liste entfernt (bleibt zur Prüfung erhalten).",
      action: () => deleteMandate.mutate(mandate.id),
    });
  };

  const handleDeleteBank = (account: BankAccountWithMandatesDto) => {
    setConfirm({
      title: "Bankverbindung löschen?",
      description: `${account.holderFirstName} ${account.holderLastName} · ${formatIbanForDisplay(
        account.iban,
      )}`,
      action: () => deleteBank.mutate(account.id),
    });
  };

  return (
    <Card className="overflow-hidden rounded-lg border border-solid border-black shadow-none">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 bg-slate-50/60 py-3">
        <div>
          <CardTitle className="flex items-center gap-1 text-lg font-semibold text-slate-800">
            <Landmark className="h-5 w-5 text-slate-500" />
            SEPA-Bankverbindungen &amp; Mandate
          </CardTitle>
          <p className="mt-1 text-sm text-slate-500">
            Bankverbindungen und Lastschriftmandate des Patienten verwalten.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="sepa-show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label htmlFor="sepa-show-archived" className="text-sm text-slate-600">
              Archivierte anzeigen
            </Label>
          </div>
          <Button
            onClick={() => setMandateDialogOpen(true)}
            size="sm"
            className="h-7 gap-1 rounded-lg border-0 bg-blue-darkest px-2.5 text-sm font-medium text-white shadow-[0_1px_2px_rgba(65,137,168,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-all duration-200 hover:from-[#4f9cbb] hover:to-[#3a7f9c] active:scale-[0.97] [&_svg]:size-3"
          >
            <Plus className="h-3 w-3" />
            Neues Mandat
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {isLoading ? (
          <div className="space-y-1">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded border border-dashed border-slate-200 text-center">
            <p className="text-sm font-medium text-slate-600">
              {showArchived ? "Keine archivierten Mandate" : "Keine Bankverbindungen hinterlegt"}
            </p>
            <p className="text-sm text-slate-400">
              {showArchived
                ? "Verwendete, widerrufene oder abgelaufene Mandate erscheinen hier."
                : "Legen Sie ein neues Mandat an, um eine Bankverbindung zu hinterlegen."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-slate-200 bg-slate-100 hover:bg-slate-100">
                  <TableHead className="w-8" />
                  <TableHead className="h-6 py-0 text-center text-sm font-semi-bold uppercase tracking-wide text-slate-600">
                    Kontoinhaber
                  </TableHead>
                  <TableHead className="h-6 py-0 text-center text-sm font-semi-bold uppercase tracking-wide text-slate-600">
                    IBAN
                  </TableHead>
                  <TableHead className="h-6 py-0 text-center text-sm font-semi-bold uppercase tracking-wide text-slate-600">
                    Art
                  </TableHead>
                  <TableHead className="h-6 py-0 text-center text-sm font-semi-bold uppercase tracking-wide text-slate-600">
                    Status
                  </TableHead>
                  <TableHead className="h-6 py-0 text-center text-sm font-semi-bold uppercase tracking-wide text-slate-600">
                    Mandant
                  </TableHead>
                  <TableHead className="h-6 w-[80px] py-0 text-center text-sm font-semi-bold uppercase tracking-wide text-slate-600">
                    Aktionen
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ account, mandate }, index) => (
                  <SepaMandateRow
                    key={mandate ? mandate.id : `bank-${account.id}`}
                    account={account}
                    mandate={mandate}
                    accountName={mandate ? accountNameById.get(mandate.accountId) : undefined}
                    index={index}
                    expanded={!!mandate && expandedId === mandate.id}
                    onToggle={() =>
                      mandate && setExpandedId(expandedId === mandate.id ? null : mandate.id)
                    }
                    busy={busy}
                    onDownloadForm={handleDownloadForm}
                    onDownloadVersion={handleDownloadVersion}
                    onActivate={(m, acc) => setActivate({ mandate: m, account: acc })}
                    onEdit={(m, acc) => setEdit({ mandate: m, account: acc })}
                    onRevoke={handleRevoke}
                    onDeleteMandate={handleDeleteMandate}
                    onDeleteBank={handleDeleteBank}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <MandateFormDialog
        patientId={patientId}
        open={mandateDialogOpen}
        onOpenChange={setMandateDialogOpen}
      />

      <SignedMandateUploadDialog
        open={!!activate}
        onOpenChange={(o) => !o && setActivate(null)}
        subtitle={
          activate
            ? `${activate.account.holderFirstName} ${activate.account.holderLastName} · ${formatIbanForDisplay(
                activate.account.iban,
              )}`
            : undefined
        }
        isPending={uploadSigned.isPending}
        isReplacement={!!activate && activate.mandate.status !== MandateStatusEnum.Draft}
        onSubmit={submitActivation}
      />

      {edit && (
        <MandateEditDialog
          patientId={patientId}
          mandateId={edit.mandate.id}
          account={edit.account}
          open={!!edit}
          onOpenChange={(o) => !o && setEdit(null)}
        />
      )}

      <RevokeMandateDialog
        open={!!revoke}
        onOpenChange={(o) => !o && setRevoke(null)}
        isPending={revokeMandate.isPending}
        onSubmit={submitRevoke}
      />

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                confirm?.action();
                setConfirm(null);
              }}
            >
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default SepaBankAccountsCard;
